import React, { createContext, FC, ReactNode, useContext } from 'react';
import styled from 'styled-components';
import { T } from 'src/shared/helpers';
import { SearchInput } from '../../../../../widgets/input';
import { FilterIcon } from '../../../../../widgets/icons/scene';
import SceneEditorActionBar, { SceneEditorAction } from './sceneEditorActionBar';

// IL GUSCIO DI UNA SEZIONE, UNO SOLO PER TUTTE. Oggetti, luci, camere, materiali, texture e
// ambienti si aprono nello stesso modo: la barra dei comandi in cima, la riga di ricerca
// sotto, e il contenuto — un albero o una griglia — che prende quello che resta.
//
// Sta scritto una volta perché le tre misure che contano — l'altezza della barra, i margini
// della riga di ricerca, il rientro del contenuto — devono essere le stesse in tutte le
// sezioni: passando da una all'altra i comandi non si spostano di due pixel. Sei copie di
// queste regole sono sei occasioni di disallineamento, e si vedono tutte insieme
// commutando il rail.
//
// LA RICERCA STA QUI, non dentro l'albero. L'albero è il contenuto della sezione, e il
// campo che lo filtra appartiene alla sezione: nella griglia dei materiali l'albero non
// c'è, e la ricerca deve restare dov'era.

/**
 * DOVE STA LA SEZIONE, non chi è. Nella colonna la sezione è alta e stretta, nella striscia
 * sotto il palco è bassa e larga: cambia una cosa sola — dove va la ricerca — e la sezione
 * non deve saperne altro. Passa da un contesto e non da una prop perché fra lo Scene Editor e
 * la sezione c'è il rail, che le monta per interfaccia e non le conosce una per una.
 */
export type SectionPlacement = 'panel' | 'strip';

const SectionPlacementContext = createContext<SectionPlacement>('panel');

export const SectionPlacementProvider = SectionPlacementContext.Provider;

export const useSectionPlacement = () => useContext(SectionPlacementContext);

const Panel = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
`;

const ToolsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
`;

const SearchField = styled.div<{ $inline?: boolean }>`
    flex: ${(props) => (props.$inline ? '0 1 220px' : '1')};
    min-width: ${(props) => (props.$inline ? '140px' : '0')};
`;

// «All» non è ancora un filtro: il dato su cui filtrare — a cosa serve un asset, dove è
// usato — non esiste (docs/scene-editor-new-sections.md § 6). Il posto però c'è, e il
// bottone dice cosa diventerà invece di sparire e far riprogettare la riga.
const FilterButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    flex: 0 0 auto;
    height: 30px;
    padding: 0 10px;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 6px;
    background: ${(props) => props.theme.app.backgroundColor};
    color: ${(props) => props.theme.app.primary};
    font-size: 12px;
    cursor: not-allowed;

    svg {
        width: 14px;
        height: 14px;
    }
`;

const Content = styled.div`
    flex: 1;
    min-height: 0;
    padding: 0 10px 12px;
    overflow-y: auto;
`;

export interface SectionPanelProps {
    actions: SceneEditorAction[];
    /** Chiude la barra dei comandi a destra: le viste della libreria, non un comando. */
    trailing?: ReactNode;
    filter: string;
    onFilterChange: (value: string) => void;
    searchPlaceholder: string;
    /** Il filtro d'uso: lo mostrano le tre sezioni della libreria, non le liste di scena. */
    showUsageFilter?: boolean;
    /**
     * L'elenco si sceglie a mucchio, e la nota lo insegna. Sta in coda e non in cima perché è
     * una cosa che si scopre lavorando, non un'istruzione da leggere prima di cominciare.
     */
    children: ReactNode;
}

const SectionPanel: FC<SectionPanelProps> = ({ actions, trailing, filter, onFilterChange, searchPlaceholder, showUsageFilter, children }) => {
    const placement = useSectionPlacement();
    const inStrip = placement === 'strip';

    const search = (
        <SearchField $inline={inStrip}>
            <SearchInput placeholder={searchPlaceholder} value={filter} onChange={(event) => onFilterChange(event.currentTarget.value)} />
        </SearchField>
    );

    const usageFilter = showUsageFilter ? (
        <FilterButton type='button' disabled aria-label={T._('Filter by usage — not available yet', 'Admin')}>
            <FilterIcon />
            {T._('All', 'Admin')}
        </FilterButton>
    ) : null;

    return (
        <Panel>
            {/* Nella striscia la riga dei comandi è larga e ha posto: ricerca e filtro ci
                stanno insieme, e la riga sotto sparisce invece di restare vuota. Nella
                colonna no — lì la barra dei comandi deve restare identica a quella delle
                altre liste, aggiungi a sinistra e distruggi al bordo. */}
            <SceneEditorActionBar
                actions={actions}
                trailing={
                    inStrip ? (
                        <>
                            {search}
                            {usageFilter}
                            {trailing}
                        </>
                    ) : (
                        trailing
                    )
                }
            />
            {!inStrip && (
                <ToolsRow>
                    {search}
                    {usageFilter}
                </ToolsRow>
            )}
            <Content>{children}</Content>
        </Panel>
    );
};

export default SectionPanel;
